import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Mail, Loader2, Shield, AlertCircle } from 'lucide-react';
import { AuthAPI } from '../../lib/api/auth';
import { useAuthContext } from '../../hooks/useAuthContext';
import { InfoPanel } from '../InfoPanel';
import { toast } from 'sonner';

export interface ChangePasswordPanelProps {
  /** Callback after successful operation */
  onUpdate?: () => void;
}

/**
 * ChangePasswordPanel - Domain-specific panel for password reset
 *
 * Allows authenticated users to reset their password via email.
 * Uses the same secure reset flow as unauthenticated users.
 *
 * Flow:
 * 1. User clicks "Reset Password"
 * 2. Confirmation dialog explains what will happen
 * 3. Password reset email sent to their account email
 * 4. User clicks link in email to set new password
 *
 * @example
 * <ChangePasswordPanel onUpdate={() => console.log('reset email sent')} />
 */
export function ChangePasswordPanel({ onUpdate }: ChangePasswordPanelProps) {
  const { user } = useAuthContext();
  const [showConfirm, setShowConfirm] = useState(false);

  // Password reset mutation
  const resetMutation = useMutation({
    mutationFn: async () => {
      if (!user?.email) throw new Error('No email address found');
      await AuthAPI.requestPasswordReset(user.email);
    },
    onSuccess: () => {
      setShowConfirm(false);
      onUpdate?.();
      toast.success('Password reset email sent! Check your inbox for the reset link.', {
        duration: 5000,
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to send reset email');
    },
  });

  const handleRequestReset = () => {
    setShowConfirm(true);
  };

  const handleConfirmReset = () => {
    resetMutation.mutate();
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  return (
    <InfoPanel title="Security" subtitle="Reset your account password" isEditable={false}>
      {showConfirm ? (
        <div className="space-y-4">
          {/* Confirmation Message */}
          <div className="rounded-lg bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 p-4">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-sky-600 dark:text-sky-400 shrink-0 mt-0.5" />
              <div className="space-y-2">
                <p className="text-sm font-medium text-sky-900 dark:text-sky-100">
                  Reset Password via Email
                </p>
                <p className="text-sm text-sky-800 dark:text-sky-200">
                  We'll send a secure password reset link to:
                  <br />
                  <strong>{user?.email}</strong>
                </p>
                <p className="text-xs text-sky-700 dark:text-sky-300">
                  Click the link in the email to securely set your new password. The link will
                  expire in 24 hours.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleConfirmReset}
              disabled={resetMutation.isPending}
              className="px-4 py-2 bg-sky-600 hover:bg-sky-700 disabled:bg-zinc-400 dark:disabled:bg-zinc-600 text-white font-medium rounded-lg transition-colors disabled:cursor-not-allowed flex items-center gap-2"
            >
              {resetMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4" />
                  <span>Send Reset Email</span>
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={resetMutation.isPending}
              className="px-4 py-2 bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-700 dark:hover:bg-zinc-600 text-zinc-700 dark:text-zinc-300 font-medium rounded-lg transition-colors disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3 text-sm">
            <Shield className="h-5 w-5 text-zinc-400" />
            <span className="text-zinc-600 dark:text-zinc-400">Password is set and secure</span>
          </div>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
            To change your password, we'll send a secure reset link to your email address (
            <strong>{user?.email}</strong>).
          </p>
          <button
            type="button"
            onClick={handleRequestReset}
            className="px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
          >
            <Mail className="h-4 w-4" />
            <span>Reset Password via Email</span>
          </button>
        </div>
      )}
    </InfoPanel>
  );
}
